import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/utils/supabase";

function readParams() {
  const sp = new URLSearchParams(window.location.search);
  return {
    email: sp.get("email") ?? "",
    next: sp.get("next") || "/dashboard",
    plan: sp.get("plan"),
  };
}

function postAuthPath(plan: string | null, next: string): string {
  if (plan === "pro" || plan === "team") return "/billing";
  if (next === "/onboarding" && plan) {
    return `/onboarding?plan=${encodeURIComponent(plan)}`;
  }
  return next;
}

export function ConfirmEmailPage() {
  const navigate = useNavigate();
  const [params] = useState(readParams);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [resendErr, setResendErr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const go = () => navigate({ to: postAuthPath(params.plan, params.next) });

    // Catch the redirect-back case (Supabase parses the magic token from URL).
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session && !cancelled) go();
    });

    // Initial check + 2s poll as a fallback.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session && !cancelled) go();
    });
    const id = setInterval(() => {
      supabase.auth.getSession().then(({ data }) => {
        if (data.session && !cancelled) go();
      });
    }, 2000);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      clearInterval(id);
    };
  }, [navigate, params.next, params.plan]);

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (!params.email || !code.trim()) return;
    setVerifyBusy(true);
    setVerifyErr(null);
    const { error } = await supabase.auth.verifyOtp({
      email: params.email,
      token: code.trim(),
      type: "signup",
    });
    setVerifyBusy(false);
    if (error) setVerifyErr(error.message);
    // Success path: onAuthStateChange in the effect above triggers redirect.
  }

  async function resend() {
    if (!params.email) return;
    setResendBusy(true);
    setResendErr(null);
    setResendMsg(null);
    const redirectTo = `${window.location.origin}/sign-up/confirm-email${window.location.search}`;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: params.email,
      options: { emailRedirectTo: redirectTo },
    });
    setResendBusy(false);
    if (error) setResendErr(error.message);
    else setResendMsg("Email re-sent. Check your inbox.");
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Mail className="size-5" />
            </div>
            <div>
              <CardTitle>Confirm your email</CardTitle>
              <CardDescription>
                {params.email
                  ? `We sent a link to ${params.email}.`
                  : "We sent a confirmation link to your inbox."}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Click the link in the email to finish creating your account, or
            paste the 6-digit code below. The page redirects automatically once
            confirmed.
          </p>
          <form className="space-y-2" onSubmit={verifyCode}>
            <Label htmlFor="otp">Confirmation code</Label>
            <div className="flex gap-2">
              <Input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={!params.email}
              />
              <Button type="submit" disabled={verifyBusy || !code.trim() || !params.email}>
                {verifyBusy ? "Verifying…" : "Verify"}
              </Button>
            </div>
            {verifyErr && (
              <p className="text-sm text-destructive" role="alert">{verifyErr}</p>
            )}
          </form>
          {resendMsg && <p className="text-sm text-primary">{resendMsg}</p>}
          {resendErr && (
            <p className="text-sm text-destructive" role="alert">
              {resendErr}
            </p>
          )}
          <Button
            variant="outline"
            onClick={resend}
            disabled={resendBusy || !params.email}
          >
            {resendBusy ? "Sending…" : "Resend email"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
